import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { Action } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  CalendarDays, Plus, FileText, Clock, CheckCircle2,
  Building2, Loader2, ArrowUp, ArrowDown, Group, X,
  ChevronRight, ChevronDown, Pencil, Save, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import AIScheduleDialog from '../components/scheduling/AIScheduleDialog';
import api from '../lib/api';

const SchedulesPage = () => {
  const navigate = useNavigate();
  const {
    can,
    projects,
    activeProject, 
    schedules, 
    activeSchedule,
    fetchProjects,
    loadProject,
    updateProject,
    setProjectActiveSchedule,
    loading 
  } = useProject();

  // Project editing state
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [editedProject, setEditedProject] = useState({ name: '', description: '' });
  const [savingProject, setSavingProject] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState(() => {
    return localStorage.getItem('scheduleSortField') || 'name';
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return localStorage.getItem('scheduleSortDirection') || 'asc';
  });
  
  // Grouping state
  const [groupBy, setGroupBy] = useState(() => {
    return localStorage.getItem('scheduleGroupBy') || 'none';
  });
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Schedule summary data
  const [scheduleActivities, setScheduleActivities] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Fetch activities for active schedule to compute summary stats
  useEffect(() => {
    const fetchActivities = async () => {
      if (!activeSchedule?.id) {
        setScheduleActivities([]);
        return;
      }
      setLoadingSummary(true);
      try {
        const response = await api.get(`/activities/schedule/${activeSchedule.id}`);
        setScheduleActivities(response.data || []);
      } catch (error) {
        console.error('Failed to fetch schedule activities:', error);
        setScheduleActivities([]);
      } finally {
        setLoadingSummary(false);
      }
    };
    fetchActivities();
  }, [activeSchedule?.id]);

  // Computed summary stats
  const scheduleSummary = useMemo(() => {
    const total = scheduleActivities.length;
    if (total === 0) return { totalActivities: 0, durationDays: 0, completion: 0 };

    // Find earliest start and latest finish across all activities
    let earliestStart = null;
    let latestFinish = null;
    let totalProgress = 0;

    scheduleActivities.forEach((act) => {
      const start = act.actual_start || act.planned_start;
      const finish = act.actual_finish || act.planned_finish;

      if (start) {
        const startDate = new Date(start);
        if (!earliestStart || startDate < earliestStart) earliestStart = startDate;
      }
      if (finish) {
        const finishDate = new Date(finish);
        if (!latestFinish || finishDate > latestFinish) latestFinish = finishDate;
      }

      totalProgress += (act.percent_complete || 0);
    });

    // Duration = calendar days from earliest start to latest finish (inclusive)
    let durationDays = 0;
    if (earliestStart && latestFinish) {
      durationDays = Math.round((latestFinish - earliestStart) / (1000 * 60 * 60 * 24)) + 1;
      if (durationDays < 0) durationDays = 0;
    }

    // Average completion across all activities
    const completion = total > 0 ? Math.round(totalProgress / total) : 0;

    return { totalActivities: total, durationDays, completion };
  }, [scheduleActivities]);

  // Initialize edited project when activeProject changes
  useEffect(() => {
    if (activeProject) {
      setEditedProject({
        name: activeProject.name || '',
        description: activeProject.description || ''
      });
    }
  }, [activeProject]);

  // Start editing project
  const handleStartEdit = () => {
    setEditedProject({
      name: activeProject?.name || '',
      description: activeProject?.description || ''
    });
    setIsEditingProject(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditingProject(false);
    setEditedProject({
      name: activeProject?.name || '',
      description: activeProject?.description || ''
    });
  };

  // Save project changes
  const handleSaveProject = async () => {
    if (!activeProject || !editedProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }

    setSavingProject(true);
    try {
      await updateProject(activeProject.id, {
        name: editedProject.name.trim(),
        description: editedProject.description.trim()
      });
      toast.success('Project updated successfully');
      setIsEditingProject(false);
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error('Failed to update project');
    } finally {
      setSavingProject(false);
    }
  };

  // Save sort preferences
  const handleSortChange = (field) => {
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      localStorage.setItem('scheduleSortDirection', newDirection);
    } else {
      setSortField(field);
      setSortDirection('asc');
      localStorage.setItem('scheduleSortField', field);
      localStorage.setItem('scheduleSortDirection', 'asc');
    }
  };

  // Save group preference
  const handleGroupChange = (value) => {
    setGroupBy(value);
    localStorage.setItem('scheduleGroupBy', value);
    setCollapsedGroups({});
  };

  // Toggle group collapse
  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Sort schedules
  const sortSchedules = useCallback((schedulesList) => {
    return [...schedulesList].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'start_date':
          aVal = a.start_date || a.created_at || '';
          bVal = b.start_date || b.created_at || '';
          break;
        case 'end_date':
          aVal = a.end_date || '';
          bVal = b.end_date || '';
          break;
        case 'status':
          aVal = a.status?.toLowerCase() || '';
          bVal = b.status?.toLowerCase() || '';
          break;
        default:
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDirection]);

  // Group schedules
  const groupSchedules = useCallback((schedulesList) => {
    if (groupBy === 'none') {
      return { 'All Schedules': schedulesList };
    }
    
    const groups = {};
    schedulesList.forEach(schedule => {
      let groupKey;
      switch (groupBy) {
        case 'status':
          groupKey = schedule.status ? schedule.status.charAt(0).toUpperCase() + schedule.status.slice(1) : 'Unknown';
          break;
        case 'created_date':
          const createdDate = schedule.created_at ? new Date(schedule.created_at) : null;
          if (createdDate) {
            const month = createdDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            groupKey = month;
          } else {
            groupKey = 'Unknown Date';
          }
          break;
        default:
          groupKey = 'All Schedules';
      }
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(schedule);
    });
    
    return groups;
  }, [groupBy]);

  // Process schedules with sorting and grouping
  const processedSchedules = useMemo(() => {
    const sorted = sortSchedules(schedules);
    return groupSchedules(sorted);
  }, [schedules, sortSchedules, groupSchedules]);

  // Sort field display names
  const sortFieldLabels = {
    name: 'Schedule Name',
    start_date: 'Start Date',
    end_date: 'End Date',
    status: 'Status'
  };

  // Group by display names
  const groupByLabels = {
    none: 'No Grouping',
    status: 'Status',
    created_date: 'Created Date'
  };

  // Handle AI schedule creation callback
  const handleAIScheduleCreated = async (result) => {
    if (activeProject) {
      await loadProject(activeProject.id);
      if (result.schedule_id) {
        navigate('/scheduling');
      }
    }
  };

  const handleSetActive = async (scheduleId) => {
    if (activeProject) {
      try {
        await setProjectActiveSchedule(activeProject.id, scheduleId);
      } catch (error) {
        console.error('Failed to set active schedule:', error);
      }
    }
  };

  // Handle schedule click - navigate to scheduling screen with this schedule
  const handleScheduleClick = async (schedule) => {
    try {
      // First set this schedule as active if it's not already
      if (!schedule.is_active && activeProject) {
        await setProjectActiveSchedule(activeProject.id, schedule.id);
      }
      // Navigate to the scheduling screen
      navigate('/scheduling');
    } catch (error) {
      console.error('Failed to open schedule:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="schedules-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Schedules
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage project schedules and timelines
          </p>
        </div>
        
        {can(Action.MANAGE_SCHEDULES) && activeProject && (
          <div className="flex items-center gap-2">
            <AIScheduleDialog onScheduleCreated={handleAIScheduleCreated} />
            <Button 
              className="h-9 bg-slate-900 text-white hover:bg-slate-800 font-medium gap-2"
              onClick={() => navigate(`/projects/${activeProject.id}`)}
              data-testid="new-schedule-button"
            >
              <Plus className="h-4 w-4" />
              New Schedule
            </Button>
          </div>
        )}
      </div>

      {/* Project Details (Editable) */}
      {activeProject && (
        <Card className="border-slate-200" data-testid="project-details-card">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
              <div className="flex-1 min-w-0">
                {isEditingProject ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5 block">
                        Project Name
                      </label>
                      <Input
                        value={editedProject.name}
                        onChange={(e) => setEditedProject(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter project name"
                        className="h-9"
                        data-testid="project-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5 block">
                        Description
                      </label>
                      <Textarea
                        value={editedProject.description}
                        onChange={(e) => setEditedProject(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Enter project description (optional)"
                        className="min-h-[60px] resize-none"
                        data-testid="project-description-input"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        size="sm"
                        className="h-8 gap-1.5"
                        onClick={handleSaveProject}
                        disabled={savingProject || !editedProject.name.trim()}
                        data-testid="save-project-button"
                      >
                        {savingProject ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Save className="h-3.5 w-3.5" />
                        )}
                        Save
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 gap-1.5"
                        onClick={handleCancelEdit}
                        disabled={savingProject}
                        data-testid="cancel-edit-button"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-5 w-5 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                          {activeProject.code || '—'}
                        </span>
                        <h2 className="text-base font-semibold text-slate-900 truncate min-w-0" data-testid="project-name-display">
                          {activeProject.name}
                        </h2>
                        <Badge variant="outline" className="text-[10px] shrink-0">
                          {activeProject.status || 'Active'}
                        </Badge>
                      </div>
                      {activeProject.description && (
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{activeProject.description}</p>
                      )}
                    </div>
                    {can(Action.MANAGE_SCHEDULES) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 gap-1.5 text-slate-500 hover:text-slate-700"
                        onClick={handleStartEdit}
                        data-testid="edit-project-button"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {!isEditingProject && activeSchedule && (
                <div className="flex items-center gap-2 max-w-full sm:flex-shrink-0">
                  <Badge className="h-6 gap-1 text-xs bg-slate-900 max-w-full min-w-0">
                    <CheckCircle2 className="h-3 w-3 shrink-0" />
                    <span className="truncate">{activeSchedule.name}</span>
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!activeProject ? (
        <Card className="border-slate-200">
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Building2 className="h-8 w-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No Project Selected</h3>
              <p className="text-sm text-slate-500 max-w-sm">
                Use the project selector in the header to select a project and view its schedules.
              </p>
              {projects.length === 0 && can(Action.CREATE_PROJECT) && (
                <Button
                  className="mt-4 gap-2"
                  onClick={() => navigate('/projects')}
                >
                  <Plus className="h-4 w-4" />
                  Create First Project
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div>
          {/* Schedule List */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base font-semibold text-slate-900">
                    Project Schedules
                  </CardTitle>
                  <Badge variant="outline" className="text-[10px]">
                    {schedules.length} schedule{schedules.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
                
                {/* Sort and Group Controls */}
                {schedules.length > 0 && (
                  <div className="flex items-center gap-2">
                    {/* Sort Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="schedule-sort-dropdown">
                          {sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                          {sortFieldLabels[sortField]}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuLabel className="text-xs text-slate-500">Sort by</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {Object.entries(sortFieldLabels).map(([field, label]) => (
                          <DropdownMenuItem 
                            key={field}
                            onClick={() => handleSortChange(field)}
                            className="flex items-center justify-between text-xs"
                            data-testid={`schedule-sort-option-${field}`}
                          >
                            <span>{label}</span>
                            {sortField === field && (
                              sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    {/* Group Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant={groupBy !== 'none' ? 'secondary' : 'outline'} 
                          size="sm" 
                          className="h-7 gap-1.5 text-xs"
                          data-testid="schedule-group-dropdown"
                        >
                          <Group className="h-3 w-3" />
                          {groupBy !== 'none' ? groupByLabels[groupBy] : 'Group'}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuLabel className="text-xs text-slate-500">Group by</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {Object.entries(groupByLabels).map(([key, label]) => (
                          <DropdownMenuItem 
                            key={key}
                            onClick={() => handleGroupChange(key)}
                            className={`text-xs ${groupBy === key ? 'bg-slate-100' : ''}`}
                            data-testid={`schedule-group-option-${key}`}
                          >
                            {label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    {/* Clear Grouping */}
                    {groupBy !== 'none' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-slate-500"
                        onClick={() => handleGroupChange('none')}
                        data-testid="schedule-clear-grouping"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : schedules.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                    <CalendarDays className="h-6 w-6 text-slate-300" />
                  </div>
                  <p className="text-sm font-medium text-slate-900 mb-1">No schedules created</p>
                  <p className="text-xs text-slate-400 text-center max-w-xs mb-3">
                    Create a schedule to start planning your project timeline
                  </p>
                  {can(Action.MANAGE_SCHEDULES) && (
                    <Button
                      size="sm"
                      onClick={() => navigate(`/projects/${activeProject.id}`)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Schedule
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(processedSchedules).map(([groupName, groupSchedules]) => {
                    const isCollapsed = collapsedGroups[groupName];
                    const showGroupHeader = groupBy !== 'none';
                    
                    return (
                      <div key={groupName} data-testid={`schedule-group-${groupName.toLowerCase().replace(/\s+/g, '-')}`}>
                        {showGroupHeader && (
                          <div 
                            className="flex items-center gap-2 mb-2 cursor-pointer hover:bg-slate-50 rounded-md p-2 -ml-2"
                            onClick={() => toggleGroupCollapse(groupName)}
                          >
                            {isCollapsed ? (
                              <ChevronRight className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            )}
                            <span className="text-sm font-semibold text-slate-700">{groupName}</span>
                            <Badge variant="outline" className="text-[10px]">{groupSchedules.length}</Badge>
                          </div>
                        )}
                        
                        {!isCollapsed && (
                          <div className="space-y-3">
                            {groupSchedules.map((schedule) => (
                              <div 
                                key={schedule.id} 
                                className={`flex items-center gap-4 p-4 rounded-lg border transition-colors cursor-pointer ${
                                  schedule.is_active 
                                    ? 'border-slate-900 bg-slate-50' 
                                    : 'border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                                }`}
                                onClick={() => handleScheduleClick(schedule)}
                                data-testid={`schedule-${schedule.id}`}
                              >
                                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                  schedule.is_active ? 'bg-slate-900' : 'bg-slate-200'
                                }`}>
                                  <CalendarDays className={`h-5 w-5 ${
                                    schedule.is_active ? 'text-white' : 'text-slate-500'
                                  }`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-slate-900">{schedule.name}</span>
                                    {schedule.is_active && (
                                      <Badge className="bg-slate-900 text-white text-[10px]">Active</Badge>
                                    )}
                                    <Badge variant="outline" className="text-[10px]">{schedule.status}</Badge>
                                  </div>
                                  {schedule.description && (
                                    <p className="text-xs text-slate-400 mt-0.5">{schedule.description}</p>
                                  )}
                                  <div className="flex items-center gap-4 mt-1">
                                    <span className="text-xs text-slate-400">
                                      Created {formatDate(schedule.created_at)}
                                    </span>
                                  </div>
                                </div>
                                {can(Action.MANAGE_SCHEDULES) && !schedule.is_active && (
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="h-8"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetActive(schedule.id);
                                    }}
                                    data-testid={`activate-schedule-${schedule.id}`}
                                  >
                                    Set Active
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedule Summary Cards */}
      {activeProject && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Activities</p>
                  <p className="text-xl font-semibold text-slate-900 tabular-nums">
                    {loadingSummary ? <Loader2 className="h-5 w-5 animate-spin text-slate-300" /> : scheduleSummary.totalActivities}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Duration (Days)</p>
                  <p className="text-xl font-semibold text-slate-900 tabular-nums">
                    {loadingSummary ? <Loader2 className="h-5 w-5 animate-spin text-slate-300" /> : scheduleSummary.durationDays}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Completion</p>
                  <p className="text-xl font-semibold text-slate-900 tabular-nums">
                    {loadingSummary ? <Loader2 className="h-5 w-5 animate-spin text-slate-300" /> : `${scheduleSummary.completion}%`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SchedulesPage;
